import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema'; // generated: npm run api:gen (from the API's OpenAPI document)
import type { ProblemDetails } from './types';
import i18n from '../i18n/i18n';

/** Access token lives in sessionStorage only (never localStorage — skill aerostores-frontend §8); refresh token is an httpOnly cookie. */
export const TOKEN_KEY = 'access_token';
/** Dispatched on window when a non-auth call answers 401; src/app/auth.ts refreshes the session or signs out. */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

const isAuthUrl = (url: string) => url.includes('/api/v1/auth/');

const readToken = (): string | null => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

// One client, bearer token from session storage, `auth:expired` on 401, problem details surfaced as ApiError (.code/.status/.fields).
// `fetch` is passed as a late-bound wrapper (not the captured global) so component tests can stub globalThis.fetch.
export const api = createClient<paths>({ baseUrl: '', fetch: (request) => fetch(request) });

const bearer: Middleware = {
  onRequest({ request }) {
    const token = readToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  onResponse({ request, response }) {
    if (response.status === 401 && !isAuthUrl(request.url)) window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    return response;
  },
};
api.use(bearer);

/** Typed error for RFC 9457 problem details returned by the API. */
/** `status` of an ApiError that never reached the server: the request failed in the browser (server down, DNS, TLS,
 *  the machine offline). It is not an HTTP status — nothing answered — so it cannot be confused with one. */
export const NO_RESPONSE = 0;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly detail: string | undefined;
  readonly fields: Record<string, string[]>;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, problem?: ProblemDetails) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${String(status)}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem?.code;
    this.detail = problem?.detail ?? undefined;
    this.fields = problem?.fields ?? {};
    this.problem = problem;
  }
}

const isProblem = (e: unknown): e is ProblemDetails => typeof e === 'object' && e !== null;

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Unwraps an openapi-fetch result: returns `data` or throws ApiError built from the problem details. */
export async function unwrap<T>(pending: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await pending.catch((e: unknown) => { throw offline(e); });
  if (data !== undefined) return data;
  if (response.ok) return data as T; // 204 No Content
  throw new ApiError(response.status, isProblem(error) ? error : undefined);
}

/**
 * A rejection from `fetch` itself means the request never arrived: there is no status and no problem details, so the
 * caller must not be told its own domain story ("could not save the issue note", "check your username and password").
 * It becomes an ApiError with NO_RESPONSE so every call site keeps one error type and can tell the two apart.
 */
const offline = (cause: unknown): ApiError =>
  cause instanceof ApiError ? cause : new ApiError(NO_RESPONSE, { detail: i18n.t('app.error.noResponse') } as ProblemDetails);

/** True when the request never reached the server, so nothing about the request itself is known to be wrong. */
export const isOffline = (e: unknown): boolean => e instanceof ApiError && e.status === NO_RESPONSE;

/**
 * User-facing message for a failed call.
 *
 * A 422 from the API carries its real sentences in `fields` — "A repair order needs at least one component",
 * "3G5350A00131: the quantity must be greater than zero" — while `detail` is the literal, useless word
 * "Validation failed". Reading `detail` alone left every form in the system saying "Validation failed" with no
 * indication of what to fix, so the field sentences are folded in here and every call site gets them at once.
 */
export const errorMessage = (e: unknown, fallback: string): string => {
  if (!(e instanceof ApiError)) return fallback;
  const fromFields = Object.values(e.fields).flat().filter(Boolean);
  if (fromFields.length > 0) return [...new Set(fromFields)].join(' · ');
  return e.detail ?? fallback;
};

/**
 * `fetch` with the bearer token for the endpoints openapi-fetch does not fit (multipart upload, text/html preview, xlsx download).
 * Same 401 → `auth:expired` behaviour as the client; non-2xx answers throw ApiError with the problem details when present.
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = readToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(url, { ...init, headers }).catch((e: unknown) => { throw offline(e); });
  if (response.status === 401 && !isAuthUrl(url)) window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  if (!response.ok) {
    let problem: ProblemDetails | undefined;
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      /* no problem body */
    }
    throw new ApiError(response.status, problem);
  }
  return response;
}

export type QueryValues = Record<string, string | number | boolean | null | undefined>;

/** `?a=1&b=x` from a record; undefined, null and '' are skipped. Keys are sent verbatim (e.g. `filter[role]`). */
export function toQuery(query: QueryValues): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * GET of a JSON endpoint whose query parameters the OpenAPI document does not declare (the paged admin lists — `page`, `pageSize`,
 * `q`, `filter[...]`): typed by the caller from the generated components until the document carries the parameters.
 */
export async function getJson<T>(path: string, query: QueryValues = {}): Promise<T> {
  const response = await authFetch(`${path}${toQuery(query)}`);
  return (await response.json()) as T;
}

/** Uploads one file as multipart/form-data (field `file`) and returns the JSON answer. */
export async function uploadFile<T>(url: string, file: File, field = 'file'): Promise<T> {
  const body = new FormData();
  body.append(field, file, file.name);
  const response = await authFetch(url, { method: 'POST', body });
  return (await response.json()) as T;
}

/** Downloads a protected file through fetch + blob (the bearer token cannot travel on a plain anchor) and saves it as `fileName`. */
export async function downloadFile(url: string, fileName: string): Promise<void> {
  await save(await authFetch(url), fileName);
}

/**
 * The same, for a file the server can only make once it has been told what to put in it — the list exports post the
 * rows the reader is looking at and get back a spreadsheet or a printed sheet of exactly those.
 */
export async function downloadPost(url: string, body: unknown, fileName: string): Promise<void> {
  const response = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  await save(response, fileName);
}

/** The server names the file when it sends a Content-Disposition; otherwise the caller's name stands. */
async function save(response: Response, fileName: string): Promise<void> {
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = named(response.headers.get('content-disposition')) ?? fileName;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

const named = (disposition: string | null): string | undefined =>
  /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '')?.[1];

/** Header carrying the single-use proof from POST /auth/pin/verify (valid 120 s) on every certifying action. */
export const SIGNATURE_PROOF_HEADER = 'X-Signature-Proof';
/** Header required by posting endpoints; one UUID per submit so a retried request cannot post twice. */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

export const newIdempotencyKey = (): string => crypto.randomUUID();

/**
 * Request init for posting / certifying calls — spread it into the openapi-fetch options:
 * `api.POST('/api/v1/.../post', { params, body, ...postingHeaders({ proof }) })`.
 * Always adds an Idempotency-Key (fresh per call unless given) and, when a PIN proof is present, X-Signature-Proof.
 */
export function postingHeaders(opts: { proof?: string; idempotencyKey?: string } = {}): { headers: Record<string, string> } {
  const headers: Record<string, string> = { [IDEMPOTENCY_KEY_HEADER]: opts.idempotencyKey ?? newIdempotencyKey() };
  if (opts.proof) headers[SIGNATURE_PROOF_HEADER] = opts.proof;
  return { headers };
}

/** Headers for a certifying call that is not a posting (e.g. approve): only the signature proof. */
export const signedHeaders = (proof: string): { headers: Record<string, string> } => ({ headers: { [SIGNATURE_PROOF_HEADER]: proof } });

// TanStack Query keys: [area, resource, id?] — invalidate ['inv','stock'] and ['inv','items'] after any posting; ['wf','inbox'] after approvals.
export const qk = {
  list: (area: string, res: string, params?: unknown) => [area, res, params] as const,
  one: (area: string, res: string, id: string | number) => [area, res, id] as const,
};
